import { AsyncBoundary } from "@components/AsyncBoundary";

import { StageRow } from "./StageRow";
import { useVisitDetail } from "./visitsQueries";

/**
 * The expanded stage tracker for one visit, shown in the recessed well
 * under an expanded patient card's band. Fetches the visit's full ordered stages
 * from `GET /visits/{uuid}`; the fetch suspends, so it's guarded with its own
 * skeleton + retry and only mounts once the card is expanded.
 */
export const FlowTracker = ({
  visitUuid,
  stale = false,
}: {
  visitUuid: string;
  /** Stale visit (the Still open strip): active marks flag instead of green. */
  stale?: boolean;
}) => (
  <AsyncBoundary
    skeleton={<TrackerSkeleton />}
    fallback={(retry) => (
      <p className="px-1 py-1 text-xs text-annotation">
        Couldn&rsquo;t load the flow.{" "}
        <button type="button" onClick={retry} className="underline">
          Retry
        </button>
      </p>
    )}
  >
    <StageList visitUuid={visitUuid} stale={stale} />
  </AsyncBoundary>
);

const StageList = ({
  visitUuid,
  stale,
}: {
  visitUuid: string;
  stale: boolean;
}) => {
  const { data: visit } = useVisitDetail(visitUuid);

  if (visit.stages.length === 0)
    return <p className="px-1 py-1 text-xs text-annotation">No stages yet.</p>;

  return (
    <div className="px-0.5 py-0.5">
      {visit.stages.map((stage) => (
        <StageRow key={stage.id} stage={stage} stale={stale} />
      ))}
    </div>
  );
};

/* Hand-rolled: these blanks stand in for stage rules, so they are drawn in the
   rule's own ink rather than the kit `bar`'s `bg-recess` well. */
const TrackerSkeleton = () => (
  <div className="space-y-2 py-1">
    {Array.from({ length: 3 }).map((_, i) => (
      <div key={i} className="h-5 animate-pulse rounded-paper bg-rule/60" />
    ))}
  </div>
);
